import {
  mediaQueryLarge,
  requestIdleCallback,
  startViewTransition,
  showLoader,
  hideLoader,
} from "@theme/utilities";
import PaginatedList from "@theme/paginated-list";
import { sectionRenderer } from "@theme/section-renderer";

/**
 * A custom element that renders a pagniated results list
 */
export default class ResultsList extends PaginatedList {
  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener("change", this.#handleMediaQueryChange);
    this.setAttribute("initialized", "");
  }

  disconnectedCallback() {
    mediaQueryLarge.removeEventListener("change", this.#handleMediaQueryChange);
  }

  /**
   * Updates the layout.
   *
   * @param {Event} event
   */
  updateLayout({ target }) {
    if (!(target instanceof HTMLInputElement)) return;

    this.#animateLayoutChange(target.value);
  }

  /**
   * Sets the layout.
   *
   * @param {string} value
   */
  #animateLayoutChange = async (value) => {
    const { grid } = this.refs;

    if (!grid) return;

    await startViewTransition(() => this.#setLayout(value), ["product-grid"]);

    requestIdleCallback(() => {
      const viewport = mediaQueryLarge.matches ? "desktop" : "mobile";
      sessionStorage.setItem(`product-grid-view-${viewport}`, value);
    });
  };

  /**
   * Animates the layout change.
   *
   * @param {string} value
   */
  #setLayout(value) {
    const { grid } = this.refs;
    if (!grid) return;
    grid.setAttribute("product-grid-view", value);
  }

  /**
   * Handles the media query change event.
   *
   * @param {MediaQueryListEvent} event
   */
  #handleMediaQueryChange = (event) => {
    const targetElement = event.matches
      ? this.querySelector('[data-grid-layout="desktop-default-option"]')
      : this.querySelector('[data-grid-layout="mobile-option"]');

    if (!(targetElement instanceof HTMLInputElement)) return;

    targetElement.checked = true;
    this.#setLayout("default");
  };

  #headerHeightCache = null;
  #headerHeightCacheTime = 0;
  #CACHE_DURATION = 100; // milliseconds

  /**
   * Gets the accurate header height accounting for sticky positioning
   * On mobile, gets .header-section which contains both header-component and search bar
   * Caches result for 100ms to reduce layout thrashing
   * @returns {number} The header height in pixels
   */
  #getHeaderHeight() {
    const now = performance.now();
    if (this.#headerHeightCache !== null && (now - this.#headerHeightCacheTime) < this.#CACHE_DURATION) {
      return this.#headerHeightCache;
    }

    const isMobile = window.innerWidth < 750;
    let height = 0;

    if (isMobile) {
      const headerSection = document.querySelector('.header-section');
      if (headerSection instanceof HTMLElement) {
        height = headerSection.offsetHeight;
      }
    } else {
      const headerHeightVar = getComputedStyle(document.documentElement).getPropertyValue('--header-height')?.trim();
      if (headerHeightVar) {
        const parsed = parseFloat(headerHeightVar);
        height = !isNaN(parsed) && parsed > 0 ? parsed : 0;
      }
    }

    if (height === 0) {
      const header = document.querySelector('header-component');
      if (header instanceof HTMLElement) {
        height = header.offsetHeight;
      }
    }

    this.#headerHeightCache = height;
    this.#headerHeightCacheTime = now;
    return height;
  }

  /**
   * Handles pagination control clicks - preserves all query parameters
   * This matches the pattern from quick-order-list.js and paginated-list.js
   * @param {Object<string, string>} data - URL search params (e.g., { page: "2" })
   * @param {Event} event - The click event
   */
  async onPaginationControlClick(data, event) {
    event.preventDefault();
    const sectionId = this.sectionId;

    if (!sectionId) return;

    const loaderElement = showLoader();

    try {
      const newURL = new URL(window.location.href);
      const browserURL = new URL(window.location.href);
      const grid = this.refs.grid;
      const batchSubpages = Number(grid?.dataset.batchSubpages);
      const lastShopifyPage = Number(grid?.dataset.lastPage);
      const isBatchMode =
        grid?.dataset.batchBlockEnd != null &&
        grid.dataset.batchBlockEnd !== '' &&
        Number.isFinite(batchSubpages) &&
        batchSubpages > 0 &&
        Number.isFinite(lastShopifyPage) &&
        lastShopifyPage > 0;

      if (data.page) {
        const pageNumber = parseInt(data.page, 10);
        if (Number.isFinite(pageNumber) && pageNumber >= 1) {
          if (isBatchMode) {
            // UI page numbers are logical pages; section rendering still needs Shopify subpage numbers.
            const logicalTotalPages = Math.max(
              1,
              Math.ceil(lastShopifyPage / batchSubpages)
            );
            const logicalPage =
              pageNumber <= logicalTotalPages
                ? pageNumber
                : Math.floor((pageNumber - 1) / batchSubpages) + 1;
            const clampedLogicalPage = Math.min(
              logicalTotalPages,
              Math.max(1, logicalPage)
            );
            const renderPage = (clampedLogicalPage - 1) * batchSubpages + 1;

            newURL.searchParams.set("page", String(renderPage));
            browserURL.searchParams.set("page", String(clampedLogicalPage));
          } else {
            newURL.searchParams.set("page", String(pageNumber));
            browserURL.searchParams.set("page", String(pageNumber));
          }
        }
      }

      newURL.hash = "";
      browserURL.hash = "";

      await sectionRenderer.renderSection(sectionId, {
        cache: !isBatchMode,
        url: newURL,
      });

      requestIdleCallback(() => {
        const rect = this.getBoundingClientRect();
        const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
        let targetY = rect.top + scrollTop;
        const headerHeight = this.#getHeaderHeight();
        targetY = targetY - headerHeight;

        window.scrollTo({
          top: Math.max(0, targetY),
          behavior: "smooth"
        });
      });

      // Keep browser URL on logical page values for shareable/predictable pagination.
      if (browserURL.toString() !== window.location.href) {
        window.history.replaceState({}, "", browserURL.toString());
      }

      this.reinitializeBatchAfterSectionUpdate();

    } catch (error) {
      console.error('Error during pagination:', error);
    } finally {
      hideLoader(loaderElement);
    }
  }
}

if (!customElements.get("results-list")) {
  customElements.define("results-list", ResultsList);
}
